import { useSiteSettings } from '@/contexts/SiteSettingsContext';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEOHead } from '@/components/shared/SEOHead';
import { useTranslation } from 'react-i18next';

export function TeamLeaderReportPage() {
  const { links } = useSiteSettings();
  const { t } = useTranslation();
  const [showGaps, setShowGaps] = useState(false);

  return (
    <main className="pt-20">
      <SEOHead 
        title="What's inside the FIVE LENS Team Leader Report | FIVE LENS People Development"
        description="A plain-English preview of the FIVE LENS Team Leader Report: your own profile laid over your team's, what the gaps mean, and how the leadership pages are structured."
        canonicalUrl="/reports/team-leader"
      />
      <div className="pfrl">

        {/* ============================ HERO ============================ */}
        <header className="pfrl__hero">
          <div className="pfrl__wrap">
            <nav className="pfrl-tier" aria-label="Team report versions">
              <span className="pfrl-tier__label">Report</span>
              <Link className="pfrl-tier__item" to="/reports/team">&larr; Team Report</Link>
              <span className="pfrl-tier__item pfrl-tier__item--on" aria-current="page">Team Leader Report</span>
            </nav>
            <p className="pfrl__eyebrow">FIVE LENS People Development &middot; Team Leader Report &middot; Preview</p>
            <h1>Your profile, laid over your team's.</h1>
            <p className="pfrl__lede">The Team Leader Report is a short, private companion to the Team Report. It takes your own two dominant styles and reads them against the group you're actually leading &mdash; not against leadership in general. The useful part isn't your profile or theirs. It's the distance between them.</p>

            <figure className="pfrl-chart">
              <div className="pfrl-chart__top">
                <div className="pfrl-chart__key">
                  <span><i style={{background:"linear-gradient(90deg,var(--rule-1),var(--rule-3))"}}></i>The team</span>
                  <span><i style={{background:"var(--ground-deep)"}}></i>You</span>
                </div>
                <button 
                  className="pfrl-toggle" 
                  type="button" 
                  aria-pressed={showGaps}
                  onClick={() => setShowGaps(!showGaps)}
                >
                  {showGaps ? 'Show the whole chart' : 'Show the widest gaps'}
                </button>
              </div>

              <div className={`pfrl-plot ${showGaps ? 'is-gaps' : ''}`} role="img" aria-label="Illustrative chart comparing a team's profile with its leader's across the nine Enneagram styles. The largest differences fall on styles 5, 7 and 8.">
                <div className="pfrl-col" style={{"--hue":"var(--e1)"} as React.CSSProperties}><div className="pfrl-bar pfrl-bar--team" style={{height:"34%"}}></div><div className="pfrl-bar pfrl-bar--leader" style={{height:"5%"}}></div></div>
                <div className="pfrl-col" style={{"--hue":"var(--e2)"} as React.CSSProperties}><div className="pfrl-bar pfrl-bar--team" style={{height:"32%"}}></div><div className="pfrl-bar pfrl-bar--leader" style={{height:"23%"}}></div></div>
                <div className="pfrl-col" style={{"--hue":"var(--e3)"} as React.CSSProperties}><div className="pfrl-bar pfrl-bar--team" style={{height:"66%"}}></div><div className="pfrl-bar pfrl-bar--leader" style={{height:"96%"}}></div></div>
                <div className="pfrl-col" style={{"--hue":"var(--e4)"} as React.CSSProperties}><div className="pfrl-bar pfrl-bar--team" style={{height:"62%"}}></div><div className="pfrl-bar pfrl-bar--leader" style={{height:"12%"}}></div></div>
                <div className="pfrl-col is-gap" style={{"--hue":"var(--e5)"} as React.CSSProperties}><div className="pfrl-bar pfrl-bar--team" style={{height:"92%"}}></div><div className="pfrl-bar pfrl-bar--leader" style={{height:"35%"}}></div></div>
                <div className="pfrl-col" style={{"--hue":"var(--e6)"} as React.CSSProperties}><div className="pfrl-bar pfrl-bar--team" style={{height:"87%"}}></div><div className="pfrl-bar pfrl-bar--leader" style={{height:"28%"}}></div></div>
                <div className="pfrl-col is-gap" style={{"--hue":"var(--e7)"} as React.CSSProperties}><div className="pfrl-bar pfrl-bar--team" style={{height:"21%"}}></div><div className="pfrl-bar pfrl-bar--leader" style={{height:"82%"}}></div></div>
                <div className="pfrl-col is-gap" style={{"--hue":"var(--e8)"} as React.CSSProperties}><div className="pfrl-bar pfrl-bar--team" style={{height:"46%"}}></div><div className="pfrl-bar pfrl-bar--leader" style={{height:"96%"}}></div></div>
                <div className="pfrl-col" style={{"--hue":"var(--e9)"} as React.CSSProperties}><div className="pfrl-bar pfrl-bar--team" style={{height:"53%"}}></div><div className="pfrl-bar pfrl-bar--leader" style={{height:"9%"}}></div></div>
              </div>
              <div className="pfrl-xaxis" aria-hidden="true">
                <span>1</span><span>2</span><span>3</span><span>4</span><span>5</span><span>6</span><span>7</span><span>8</span><span>9</span>
              </div>

              <p className="pfrl-chart__note">Illustrative data. Nine styles across the bottom, the team in colour and the leader in grey. Here the leader is far more forceful and far more future-focused than the group, while the team holds most of the caution and most of the quiet expertise. <strong>Those three columns are the whole conversation.</strong></p>
            </figure>

            <ul className="pfrl__hero-meta">
              <li>Team Leader Report</li>
              <li>The leader only</li>
              <li>6 pages</li>
              <li>Two leadership styles</li>
              <li>Companion to the Team Report</li>
            </ul>
          </div>
        </header>

        {/* ============================ HOW TO READ IT ============================ */}
        <section className="pfrl__section">
          <div className="pfrl__wrap">
            <div className="pfrl__head">
              <div className="pfrl__head-row">
                <p className="pfrl__eyebrow">01 &mdash; Reading the overlay</p>
                <div className="pfrl__ticks"></div>
              </div>
              <h2>Three ways a column can read</h2>
              <p className="pfrl__lede">Only three shapes matter, and each one means something different for how you run the team.</p>
            </div>

            <ul className="pfrl-rules">
              <li>
                <b>You're high, they're low</b>
                <p>Something you do naturally, nobody else does. It's your contribution &mdash; and the thing you'll be tempted to demand of people who can't supply it. Usually where frustration starts.</p>
              </li>
              <li>
                <b>They're high, you're low</b>
                <p>A strength sitting in the room that you may be discounting because it isn't how you work. This is the column that most often turns out to have been the missed opportunity.</p>
              </li>
              <li>
                <b>You match</b>
                <p>Easy alignment, and a shared blind spot. When leader and team lean the same way, nobody in the room is positioned to notice what the whole group is missing.</p>
              </li>
            </ul>
          </div>
        </section>

        {/* ============================ WHAT'S IN IT ============================ */}
        <section className="pfrl__section">
          <div className="pfrl__wrap">
            <div className="pfrl__head">
              <div className="pfrl__head-row">
                <p className="pfrl__eyebrow">02 &mdash; What's in it</p>
                <div className="pfrl__ticks"></div>
              </div>
              <h2>Six pages, and most of it is about them</h2>
            </div>

            <ol className="pfrl-chapters">
              <li className="pfrl-chapter pfrl-chapter--1">
                <div className="pfrl-chapter__num">Pg. 04</div>
                <div><h3>Team and leader profiles</h3><p className="pfrl-chapter__q"><em>Where do you and the group diverge?</em></p></div>
                <div className="pfrl-chapter__what">The overlay chart across all nine styles. Your team receives a copy of this same chart in their report, so it isn't a private document &mdash; it's the shared starting point that the rest of your report interprets.</div>
              </li>
              <li className="pfrl-chapter pfrl-chapter--2">
                <div className="pfrl-chapter__num">Pg. 05</div>
                <div><h3>Your primary leadership style</h3><p className="pfrl-chapter__q"><em>What kind of leader do you default to?</em></p></div>
                <div className="pfrl-chapter__what">Your dominant style, named and described as a leadership style rather than a personality: what your focus tends to be, where your natural approach helps, and where it costs you. The scale shows how far towards that style you sit &mdash; and the far end of every scale is unflattering, deliberately.</div>
              </li>
              <li className="pfrl-chapter pfrl-chapter--3">
                <div className="pfrl-chapter__num">Pg. 05&ndash;06</div>
                <div><h3>Your leadership competencies</h3><p className="pfrl-chapter__q"><em>What does this style make you good at?</em></p></div>
                <div className="pfrl-chapter__what">Three named capabilities that come with each style, defined in ordinary terms &mdash; the things you can rely on yourself to do well under pressure. They're the counterweight to the caution in the rest of the page.</div>
              </li>
              <li className="pfrl-chapter pfrl-chapter--4">
                <div className="pfrl-chapter__num">Pg. 05&ndash;06</div>
                <div><h3>Leader versus team, with pointers</h3><p className="pfrl-chapter__q"><em>So what do you actually do on Monday?</em></p></div>
                <div className="pfrl-chapter__what">The most practical part of the report: a short read on how your style meets this particular group, followed by four or five specific pointers for leading them. Written for your team's actual composition, not for leaders in general &mdash; a different team would produce different pointers from the same profile.</div>
              </li>
            </ol>
          </div>
        </section>

        {/* ============================ SAMPLE PAGE ============================ */}
        <section className="pfrl__section">
          <div className="pfrl__wrap">
            <div className="pfrl__head">
              <div className="pfrl__head-row">
                <p className="pfrl__eyebrow">03 &mdash; What a page looks like</p>
                <div className="pfrl__ticks"></div>
              </div>
              <h2>Your style on the left, your team on the right</h2>
              <p className="pfrl__lede">The written feedback and the pointers are held back here &mdash; that part belongs in your own report.</p>
            </div>

            <article className="pfrl-page">
              <div className="pfrl-page__bar">
                <div className="pfrl-page__brand">FIVE LENS<span className="pfrl-page__tier">Team Leader Report</span></div>
                <div className="pfrl-page__title">
                  <strong>Leader's Primary Style</strong>
                  <span>Your default way of taking charge</span>
                </div>
              </div>
              <div className="pfrl-page__cols">
                <div className="pfrl-page__col">
                  <p className="pfrl-page__label">Your reading</p>
                  <div className="pfrl-scale__rail">
                    <div className="pfrl-scale__tmark" style={{"--pos":"88%"} as React.CSSProperties}><span>T</span></div>
                    <div className="pfrl-scale__band" style={{"--hue":"var(--rule-5)"} as React.CSSProperties}></div>
                    <div className="pfrl-scale__rule"></div>
                  </div>
                  <div className="pfrl-page__anchors">
                    <div><b>Recessive</b><ul><li>Indecisive</li><li>Overly accommodating</li><li>Powerless</li></ul></div>
                    <div><b>Dominant</b><ul><li>Domineering</li><li>Hard-hearted</li><li>Ruthless</li></ul></div>
                  </div>
                  <ul className="pfrl-redact">
                    <li><span></span><span style={{width:"74%"}}></span></li>
                    <li><span></span><span style={{width:"58%"}}></span></li>
                    <li><span></span><span style={{width:"81%"}}></span></li>
                  </ul>
                  <p className="pfrl-page__stamp">What sitting this far along the scale tends to suggest about how you work.</p>
                </div>

                <div className="pfrl-page__col">
                  <p className="pfrl-page__label">Your style, described</p>
                  <ul className="pfrl-redact">
                    <li><span></span><span></span><span style={{width:"52%"}}></span></li>
                    <li><span></span><span style={{width:"69%"}}></span></li>
                  </ul>
                  <p className="pfrl-page__label" style={{marginTop:"26px"}}>Leader style competencies</p>
                  <ol className="pfrl-comp">
                    <li><b>Competency one</b><div className="pfrl-lines"><span></span><span style={{width:"63%"}}></span></div></li>
                    <li><b>Competency two</b><div className="pfrl-lines"><span></span><span style={{width:"78%"}}></span></div></li>
                    <li><b>Competency three</b><div className="pfrl-lines"><span></span><span style={{width:"55%"}}></span></div></li>
                  </ol>
                  <p className="pfrl-page__stamp">Three named capabilities that come with this style.</p>
                </div>

                <div className="pfrl-page__col pfrl-page__col--aside">
                  <p className="pfrl-page__label">Leader vs team</p>
                  <ul className="pfrl-redact"><li><span></span><span></span><span style={{width:"58%"}}></span></li></ul>
                  <p className="pfrl-page__label" style={{marginTop:"24px"}}>Pointers for leading this team</p>
                  <ul className="pfrl-redact">
                    <li><span></span><span style={{width:"70%"}}></span></li>
                    <li><span></span><span style={{width:"84%"}}></span></li>
                    <li><span></span><span style={{width:"61%"}}></span></li>
                  </ul>
                  <p className="pfrl-page__stamp">Four or five specific things to do differently with this particular group.</p>
                </div>
              </div>
            </article>
          </div>
        </section>

        {/* ============================ PROCESS ============================ */}
        <section className="pfrl__section">
          <div className="pfrl__wrap">
            <div className="pfrl__head">
              <div className="pfrl__head-row">
                <p className="pfrl__eyebrow">04 &mdash; How to use it</p>
                <div className="pfrl__ticks"></div>
              </div>
              <h2>Read it after the workshop, not before</h2>
            </div>
            <ol className="pfrl-steps">
              <li><div><b>The team completes the questionnaire and is locked</b><p>Your report is calculated from the same lock date as theirs, so both documents describe the same version of the team.</p></div></li>
              <li><div><b>Run the team session first</b><p>The Team Report gets worked through with the whole group. Arriving with your leader report already read tends to make you steer the session towards conclusions you've formed on your own.</p></div></li>
              <li><div><b>Then read your own report</b><p>Start with the overlay chart and find the three widest columns. Everything else in the report is there to explain those gaps.</p></div></li>
              <li><div><b>Pick two pointers, not all of them</b><p>The pointers are specific enough to act on immediately. Choose the two that match a real situation you're facing in the next month and leave the rest.</p></div></li>
              <li><div><b>Take it to a coaching conversation</b><p>This report is written for you rather than about you, and it's blunt in places. A practitioner debrief is where the uncomfortable lines turn into something useful.</p></div></li>
            </ol>
          </div>
        </section>

        {/* ============================ CTA ============================ */}
        <section className="pfrl__section" style={{borderTop:0}}>
          <div className="pfrl__wrap">
            <div className="pfrl-cta">
              <div>
                <h2>Leading a team through this?</h2>
                <p>A certified practitioner can run the team process and debrief your leader report alongside it.</p>
              </div>
              <div className="pfrl-cta__actions">
                <a className="pfrl-btn pfrl-btn--solid" href={links.bookingUrl} target="_blank" rel="noopener noreferrer">Book a conversation</a>
                <Link className="pfrl-btn pfrl-btn--ghost" to="/reports/team">See the Team Report</Link>
              </div>
            </div>
          </div>
        </section>

        <footer className="pfrl__foot">
          <div className="pfrl__wrap">
            <p>This page is an illustrative preview. The chart, scale positions and page content shown here are for demonstration only and are not drawn from any real team or leader. The FIVE LENS Questionnaire, the Team Leader Report and its interpretive content are the property of FIVE LENS People Development (Pty) Ltd, part of Ennea International Group. &copy; FIVE LENS People Development.</p>
          </div>
        </footer>

      </div>
    </main>
  );
}
